import { getReferralsByCrmId } from 'packages/tackle-api/src/co-sell-msft';
import { useQuery } from '@tanstack/react-query';

/**
 * Custom hook to fetch Microsoft referrals for a given CRM ID using react-query
 *
 * @param options - Hook configuration options
 * @param options.crmId - The CRM ID to fetch referrals for
 * @param options.enabled - Whether the query should automatically run (defaults to false)
 * @param options.refetchInterval - Optional interval in ms to refetch data
 *
 * @returns React Query result object containing referrals data and query state
 *
 * @example
 * // Basic usage
 * const { data, isLoading } = useMsftReferralsForCrmId({
 *   crmId: "123",
 *   enabled: true
 * });
 *
 * @example
 * // With refetch interval
 * const { data } = useMsftReferralsForCrmId({
 *   crmId: "123",
 *   enabled: true,
 *   refetchInterval: 5000 // Refetch every 5 seconds
 * });
 */
export default function useMsftReferralsForCrmId({
  crmId,
  enabled = false,
  refetchInterval,
}: {
  crmId: string;
  enabled?: boolean;
  refetchInterval?: number;
}) {
  return useQuery({
    queryKey: ['msft-referrals-by-crmId', crmId],
    queryFn: () => getReferralsByCrmId({ crmId }),
    enabled: !!enabled && !!crmId,
    retry: 2,
    refetchInterval: refetchInterval ?? false,
  });
}
